import { useEffect, useState } from "react";
import { Loader2, Plus, ExternalLink, Trash2 } from "lucide-react";
import { Button, Modal, Field, Select } from "../../../shared/ui";
import {
  listAdminInvoices,
  listStripeInvoices,
  createAdminInvoice,
  setInvoiceStatus,
  listAdminWorkspaces,
  type AdminInvoice,
  type StripeInvoice,
  type Page,
} from "../../../features/admin/api/adminApi";
import { PageHeader, StatusPill, shortDate, money } from "./_shared";

type Tab = "platform" | "stripe";

export default function AdminInvoices() {
  const [tab, setTab] = useState<Tab>("platform");
  const [platform, setPlatform] = useState<Page<AdminInvoice> | null>(null);
  const [stripe, setStripe] = useState<StripeInvoice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadPlatform = () => {
    setLoading(true);
    listAdminInvoices({ limit: 50 }).then(setPlatform).catch(() => undefined).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (tab === "platform") loadPlatform();
    else { setLoading(true); listStripeInvoices(50).then(setStripe).catch(() => undefined).finally(() => setLoading(false)); }
  }, [tab]);

  const mark = async (inv: AdminInvoice, status: AdminInvoice["status"]) => {
    await setInvoiceStatus(inv._id, status);
    loadPlatform();
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Platform-issued + Stripe subscription invoices"
        actions={tab === "platform" ? <Button variant="primary" size="sm" onClick={() => setCreating(true)}><Plus size={14} /> New invoice</Button> : undefined}
      />
      <div className="p-4 sm:p-6">
        <div className="mb-4 inline-flex rounded-lg border border-ink-200 p-0.5 text-[12.5px]">
          {(["platform", "stripe"] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`rounded-md px-3 py-1.5 font-medium capitalize ${tab === t ? "bg-ink-900 text-white" : "text-ink-600"}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : tab === "platform" ? (
          <div className="surface-card overflow-x-auto p-0">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Workspace</th><th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(platform?.rows ?? []).map((inv) => (
                  <tr key={inv._id} className="border-b border-ink-100 last:border-0">
                    <td className="px-4 py-3 font-mono text-[11.5px] text-ink-700">{inv.number}</td>
                    <td className="px-4 py-3">{inv.workspace?.name ?? "—"}</td>
                    <td className="px-4 py-3 capitalize">{inv.kind.replace("_", " ")}</td>
                    <td className="px-4 py-3 text-right tabular">{money(inv.total, inv.currency)}</td>
                    <td className="px-4 py-3"><StatusPill status={inv.status} /></td>
                    <td className="px-4 py-3 text-ink-500">{shortDate(inv.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      {inv.status !== "paid" && inv.status !== "void" && (
                        <span className="flex justify-end gap-1.5">
                          <button onClick={() => mark(inv, "paid")} className="rounded-md border border-emerald/40 px-2 py-1 text-[11px] font-medium text-emerald hover:bg-emerald/5">Mark paid</button>
                          <button onClick={() => mark(inv, "void")} className="rounded-md p-1 text-ink-400 hover:text-rose" title="Void"><Trash2 size={13} /></button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(platform?.rows.length ?? 0) === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-400">No platform invoices yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="surface-card overflow-x-auto p-0">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Links</th>
                </tr>
              </thead>
              <tbody>
                {(stripe ?? []).map((inv) => (
                  <tr key={inv.id} className="border-b border-ink-100 last:border-0">
                    <td className="px-4 py-3 font-mono text-[11.5px] text-ink-700">{inv.number ?? inv.id.slice(0, 12)}</td>
                    <td className="px-4 py-3">{inv.customerName || inv.customerEmail || "—"}</td>
                    <td className="px-4 py-3 text-right tabular">{money(inv.total, inv.currency)}</td>
                    <td className="px-4 py-3"><StatusPill status={inv.status ?? "open"} /></td>
                    <td className="px-4 py-3 text-ink-500">{shortDate(inv.created)}</td>
                    <td className="px-4 py-3 text-right">
                      {inv.hostedUrl && <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gold-600 hover:text-gold-700"><ExternalLink size={12} /> View</a>}
                    </td>
                  </tr>
                ))}
                {(stripe?.length ?? 0) === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-400">No Stripe invoices (or Stripe not configured).</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && <NewInvoiceModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); loadPlatform(); }} />}
    </>
  );
}

interface LineRow { description: string; quantity: number; unitPrice: number }

function NewInvoiceModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [kind, setKind] = useState<"plan" | "top_up">("plan");
  const [lines, setLines] = useState<LineRow[]>([{ description: "", quantity: 1, unitPrice: 0 }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listAdminWorkspaces({ limit: 100 }).then((p) => setWorkspaces(p.rows.map((w) => ({ id: w.id, name: w.name })))).catch(() => undefined);
  }, []);

  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0);
  const setLine = (i: number, patch: Partial<LineRow>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!workspaceId) { setErr("Pick a workspace"); return; }
    setSaving(true);
    setErr(null);
    try {
      await createAdminInvoice({ workspaceId, kind, lineItems: lines.filter((l) => l.description.trim()), notes: notes || undefined });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="New invoice"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={save}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : null} Create invoice
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {err && <p className="rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{err}</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select label="Workspace" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}
            options={[{ value: "", label: "Select…" }, ...workspaces.map((w) => ({ value: w.id, label: w.name }))]} />
          <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as "plan" | "top_up")}
            options={[{ value: "plan", label: "Plan" }, { value: "top_up", label: "Top-up" }]} />
        </div>

        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Line items</p>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_90px_auto] items-center gap-2">
                <input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Description"
                  className="rounded-lg border border-ink-200 px-3 py-2 text-[13px] focus:border-gold-400 focus:outline-none" />
                <input type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })}
                  className="rounded-lg border border-ink-200 px-2 py-2 text-[13px] focus:border-gold-400 focus:outline-none" />
                <input type="number" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: Number(e.target.value) })} placeholder="$"
                  className="rounded-lg border border-ink-200 px-2 py-2 text-[13px] focus:border-gold-400 focus:outline-none" />
                <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} disabled={lines.length === 1} className="p-1.5 text-ink-400 hover:text-rose disabled:opacity-30"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setLines((ls) => [...ls, { description: "", quantity: 1, unitPrice: 0 }])} className="mt-2 text-[12px] font-medium text-gold-600 hover:text-gold-700">+ Add line</button>
        </div>

        <Field label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <p className="text-right text-[14px] font-semibold text-ink-900">Total: {money(total)}</p>
      </div>
    </Modal>
  );
}
